
import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Github } from 'lucide-react';

const Projects: React.FC = () => {
  const projects = [
    {
      title: "Nexus Dashboard",
      tag: "SAAS PLATFORM",
      desc: "A real-time financial monitoring system for high-frequency trading bots.",
      image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=1200&auto=format&fit=crop",
      stack: ["React / Next.js", "Financial APIs", "Tailwind CSS"]
    },
    {
      title: "Flow State",
      tag: "AUTOMATION",
      desc: "Custom automated supply-chain management for a luxury e-commerce brand.",
      image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?q=80&w=1200&auto=format&fit=crop",
      stack: ["Python Automation", "Shopify Integration", "Inventory Logic"]
    },
    {
      title: "Core Infrastructure",
      tag: "BACKEND ARCHITECTURE",
      desc: "Hardened server orchestration handling over 100k concurrent WebSocket connections.",
      image: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=1200&auto=format&fit=crop",
      stack: ["Golang Backend", "Dockerized Microservices", "Kubernetes"]
    }
  ];

  return (
    <div className="pt-40 pb-32 container mx-auto px-6">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mb-24"
      >
        <h1 className="text-5xl md:text-7xl font-display font-bold mb-8 tracking-tighter">
          Our <span className="text-accent">Selected</span> Works.
        </h1>
        <p className="text-xl text-zinc-400">
          A glimpse into the systems we've built. We focus on scalability, reliability, and sheer performance.
        </p>
      </motion.div>

      <div className="flex flex-col space-y-40">
        {projects.map((project, i) => (
          <motion.div 
            key={i}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            className={`flex flex-col ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 lg:gap-20 items-center`}
          >
            <div className="flex-1 w-full group relative perspective">
              <motion.div 
                whileHover={{ rotateY: i % 2 === 0 ? 5 : -5, rotateX: 2, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 100, damping: 20 }}
                className="aspect-video bg-zinc-900 rounded-[32px] overflow-hidden border border-white/5 shadow-2xl"
              >
                <img 
                  src={project.image} 
                  alt={project.title} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 grayscale-[0.8] group-hover:grayscale-0" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity" />
              </motion.div>
              
              <div className="absolute -bottom-6 -right-6 md:bottom-12 md:right-12 flex space-x-3">
                 <div className="bg-white/10 backdrop-blur-xl border border-white/10 p-4 rounded-2xl hover:bg-white hover:text-background transition-colors interactive shadow-2xl">
                    <ExternalLink className="w-5 h-5" />
                 </div>
                 <div className="bg-white/10 backdrop-blur-xl border border-white/10 p-4 rounded-2xl hover:bg-white hover:text-background transition-colors interactive shadow-2xl">
                    <Github className="w-5 h-5" />
                 </div>
              </div>
            </div>

            <div className="flex-1 max-w-lg">
              <div className="text-accent font-bold tracking-widest text-xs uppercase mb-4">{project.tag}</div>
              <h3 className="text-4xl md:text-5xl font-display font-bold mb-6 tracking-tight">{project.title}</h3>
              <p className="text-zinc-400 text-lg mb-8 leading-relaxed">
                {project.desc}
              </p>
              <ul className="space-y-4 mb-10">
                {project.stack.map((tech, j) => (
                  <li key={j} className="flex items-center text-sm text-zinc-300 font-medium italic">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full mr-3 shadow-[0_0_8px_rgba(52,211,153,0.6)]" />
                    {tech}
                  </li>
                ))}
              </ul>
              <motion.button 
                whileHover={{ x: 10 }}
                className="text-white font-bold flex items-center space-x-2 group interactive"
              >
                <span>Read Case Study</span>
                <div className="w-10 h-[1px] bg-accent group-hover:w-16 transition-all shadow-[0_0_8px_rgba(52,211,153,0.6)]" />
              </motion.button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default Projects;
